import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import { get, post } from '../../../../utils/shared/api';
import { useSortableData, SortableHeader, SortDirection } from '../SortableTable';

import {
  ExternalInvoice,
} from './types';

// ========================================
// Styled Components
// ========================================

const Container = styled.div`
  padding: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const TotalCount = styled.div`
  font-size: 13px;
  color: #666;
  margin-bottom: 16px;
  text-transform: uppercase;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  ${({ variant }) => variant === 'primary' ? `
    background: #00a6c2;
    color: white;
    border: none;
    
    &:hover {
      background: #0094ab;
    }
  ` : `
    background: white;
    color: #666;
    border: 1px solid #ddd;
    
    &:hover {
      background: #f8f9fa;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadingOverlay = styled.div`
  padding: 40px;
  text-align: center;
  color: #666;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
`;

const Th = styled.th<{ $sortable?: boolean; $sorted?: boolean }>`
  text-align: left;
  padding: 12px 16px;
  background: #f8f9fa;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ $sorted }) => ($sorted ? '#00a6c2' : '#666')};
  border-bottom: 1px solid #e9ecef;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;
  white-space: nowrap;
  
  &:hover {
    ${({ $sortable }) => $sortable && `
      background: #f0f0f0;
      color: #00a6c2;
    `}
  }
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
  color: #333;
`;

const StatusBadge = styled.span<{ isPaid: boolean }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  ${({ isPaid }) => isPaid ? `
    background: #e8f5e9;
    color: #2e7d32;
  ` : `
    background: #fff3e0;
    color: #f57c00;
  `}
`;

const InvoiceLink = styled(Link)`
  color: #00a6c2;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const EmptyState = styled.div`
  padding: 60px;
  text-align: center;
  color: #666;
  background: #f8f9fa;
  border-radius: 6px;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
`;

const PageButton = styled.button<{ active?: boolean }>`
  padding: 8px 12px;
  background: ${({ active }) => active ? '#00a6c2' : '#fff'};
  color: ${({ active }) => active ? '#fff' : '#333'};
  border: 1px solid ${({ active }) => active ? '#00a6c2' : '#ddd'};
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  
  &:hover:not(:disabled) {
    background: ${({ active }) => active ? '#0094ab' : '#f8f9fa'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ProgressBar = styled.div`
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #e3f2fd;
  border-top-color: #1976d2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ProgressText = styled.span`
  font-size: 14px;
  color: #1565c0;
`;

// ========================================
// Utility Functions
// ========================================

const formatMoney = (amount: number | null | undefined): string => {
  if (amount == null) return '-';
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return dateStr;
  }
};

const formatPubId = (pubId: string | null | undefined): string => {
  if (!pubId) return '-';
  return pubId.includes(',') ? pubId.replace(/,/g, ', ') : pubId;
};

const PAGE_SIZE = 10;

// Poll batch status using REST API
const pollBatch = (bid: string, callback: () => void) => {
  get(`/api/v1/batches/${bid}`, true).then((value: { isComplete: boolean }) => {
    if (value.isComplete) {
      callback();
    } else {
      setTimeout(() => pollBatch(bid, callback), 1000);
    }
  }).catch(() => {
    // If batch status check fails, still try to refresh data
    callback();
  });
};

// ========================================
// Component
// ========================================

// Sort indicator component
const SortIndicator: React.FC<{ direction: SortDirection }> = ({ direction }) => {
  if (!direction) return null;
  return <span style={{ marginLeft: '4px' }}>{direction === 'asc' ? '↓' : '↑'}</span>;
};

const ExternalInvoices: React.FC = () => {
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [invoices, setInvoices] = useState<ExternalInvoice[]>([]);
  const mountedRef = useRef(true);
  
  // Sortable data hook
  const { sortedData, requestSort, getSortDirection } = useSortableData(invoices, { key: 'id', direction: 'desc' });

  // Fetch invoices from REST API
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // get() with isJsonApi=true (default) deserializes JSON:API response
      const data = await get('/api/v2/external_invoices', true);
      if (mountedRef.current) {
        setInvoices(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch external invoices:', error);
      if (mountedRef.current) {
        setInvoices([]);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Load invoices on mount
  useEffect(() => {
    mountedRef.current = true;
    fetchData();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  // Trigger NetSuite invoice fetch
  const handleFetchInvoices = useCallback(async () => {
    setFetching(true);
    try {
      const response = await post('/api/v2/fetch_invoices', null, false);
      if (response?.data?.id) {
        toast.info('Fetching invoices from NetSuite...');
        pollBatch(response.data.id, () => {
          if (mountedRef.current) {
            fetchData();
            setFetching(false);
            toast.success('Invoices fetched successfully!');
          }
        });
      } else {
        setFetching(false);
        toast.error('Failed to start invoice fetch');
      }
    } catch (error: any) {
      setFetching(false);
      toast.error(error?.message || 'Failed to fetch invoices');
    }
  }, [fetchData]);

  const totalPages = Math.ceil(sortedData.length / PAGE_SIZE);
  const paginatedInvoices = sortedData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const isLoading = loading || fetching;

  return (
    <Container>
      <HeaderRow>
        <Title>External Invoices</Title>
        <Button variant="primary" onClick={handleFetchInvoices} disabled={isLoading}>
          {fetching ? 'Fetching...' : 'Fetch Invoices'}
        </Button>
      </HeaderRow>

      {fetching && (
        <ProgressBar>
          <Spinner />
          <ProgressText>
            Fetching invoices from NetSuite...
          </ProgressText>
        </ProgressBar>
      )}

      {loading ? (
        <LoadingOverlay>Loading invoices...</LoadingOverlay>
      ) : invoices.length === 0 ? (
        <EmptyState>
          <p>No external invoices found.</p>
          <p>Click "Fetch Invoices" to retrieve invoices from NetSuite.</p>
        </EmptyState>
      ) : (
        <>
          <TotalCount>{invoices.length} Total External_Invoices</TotalCount>
          <Table>
            <thead>
              <tr>
                <Th $sortable $sorted={getSortDirection('id') !== null} onClick={() => requestSort('id')}>
                  ID <SortIndicator direction={getSortDirection('id')} />
                </Th>
                <Th $sortable $sorted={getSortDirection('status') !== null} onClick={() => requestSort('status')}>
                  Status <SortIndicator direction={getSortDirection('status')} />
                </Th>
                <Th $sortable $sorted={getSortDirection('internalId') !== null} onClick={() => requestSort('internalId')}>
                  NetSuite ID <SortIndicator direction={getSortDirection('internalId')} />
                </Th>
                <Th $sortable $sorted={getSortDirection('pubId') !== null} onClick={() => requestSort('pubId')}>
                  Pub ID <SortIndicator direction={getSortDirection('pubId')} />
                </Th>
                <Th $sortable $sorted={getSortDirection('createdDate') !== null} onClick={() => requestSort('createdDate')}>
                  Created <SortIndicator direction={getSortDirection('createdDate')} />
                </Th>
                <Th $sortable $sorted={getSortDirection('lastModifiedDate') !== null} onClick={() => requestSort('lastModifiedDate')}>
                  Modified <SortIndicator direction={getSortDirection('lastModifiedDate')} />
                </Th>
                <Th $sortable $sorted={getSortDirection('memo') !== null} onClick={() => requestSort('memo')}>
                  Memo <SortIndicator direction={getSortDirection('memo')} />
                </Th>
                <Th $sortable $sorted={getSortDirection('total') !== null} onClick={() => requestSort('total')}>
                  Total <SortIndicator direction={getSortDirection('total')} />
                </Th>
              </tr>
            </thead>
            <tbody>
              {paginatedInvoices.map((invoice) => (
                <tr key={invoice.id}>
                  <Td>
                    <InvoiceLink to={`/admin2/external_invoices/${invoice.id}`}>
                      {invoice.id}
                    </InvoiceLink>
                  </Td>
                  <Td>
                    <StatusBadge isPaid={invoice.status?.includes('Paid') || false}>
                      {invoice.status || '-'}
                    </StatusBadge>
                  </Td>
                  <Td>{invoice.internalId || '-'}</Td>
                  <Td>{formatPubId(invoice.pubId)}</Td>
                  <Td>{formatDate(invoice.createdDate)}</Td>
                  <Td>{formatDate(invoice.lastModifiedDate)}</Td>
                  <Td>{invoice.memo || '-'}</Td>
                  <Td>{formatMoney(invoice.total)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <Pagination>
              <PageButton
                onClick={() => setPage(p => p - 1)}
                disabled={page === 0}
              >
                ← Previous
              </PageButton>
              <span>
                Page {page + 1} of {totalPages}
              </span>
              <PageButton
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
              >
                Next →
              </PageButton>
            </Pagination>
          )}
        </>
      )}
    </Container>
  );
};

export default ExternalInvoices;
